<script setup lang="ts">
/**
 * Il finto tavolo d'asta accanto al titolo.
 *
 * È un disegno, non un'anteprima interattiva: nomi inventati, numeri fermi,
 * countdown a un fotogramma qualsiasi. Serve a far capire in un colpo d'occhio
 * cosa si vede dal telefono durante una chiamata — chi è in gara, a che prezzo,
 * quanto tempo resta — senza che la copertina debba parlare con l'API.
 *
 * `aria-hidden`: per chi usa uno screen reader questa scatola non aggiunge
 * niente al testo accanto, e letta voce per voce sarebbe solo rumore.
 */
</script>

<template>
  <div class="mock" aria-hidden="true">
    <div class="mock__bar">
      <span class="mock__dot" />
      <span class="mock__live">Chiamata in corso</span>
      <span class="mock__budget">Crediti 328</span>
    </div>

    <div class="mock__player">
      <span class="mock__role">A</span>
      <div>
        <p class="mock__name">Ferrari</p>
        <p class="mock__team">Aurora</p>
      </div>
      <div class="mock__price">
        <span class="mock__amount">34</span>
        <span class="mock__by">offerta di Luca</span>
      </div>
    </div>

    <div class="mock__gauge"><span class="mock__gauge-fill" /></div>
    <p class="mock__timer">1,8 s</p>

    <div class="mock__bids">
      <span class="mock__bid">+1</span>
      <span class="mock__bid">+5</span>
      <span class="mock__bid mock__bid--strong">+10</span>
    </div>

    <ul class="mock__log">
      <li><b>Bianchi</b> a Marta — 21</li>
      <li><b>Conti</b> a Luca — 47</li>
      <li><b>De Santis</b> invenduto</li>
    </ul>
  </div>
</template>

<style scoped>
.mock {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: 1rem;
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.mock__bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.mock__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.mock__live {
  color: var(--color-accent-text);
  font-weight: 700;
  letter-spacing: 0.02em;
}

.mock__budget {
  margin-left: auto;
  color: var(--color-muted);
  font-variant-numeric: tabular-nums;
}

.mock__player {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.9rem;
}

.mock__role {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-md);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: 800;
}

.mock__name {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}

.mock__team {
  color: var(--color-faint);
  font-size: 0.8125rem;
}

.mock__price {
  margin-left: auto;
  text-align: right;
}

.mock__amount {
  display: block;
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.mock__by {
  color: var(--color-faint);
  font-size: 0.75rem;
}

.mock__gauge {
  height: 0.5rem;
  margin-top: 0.9rem;
  border-radius: var(--radius-pill);
  background: var(--color-sunken);
  overflow: hidden;
}

.mock__gauge-fill {
  display: block;
  width: 58%;
  height: 100%;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.mock__timer {
  margin-top: 0.35rem;
  color: var(--color-muted);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.mock__bids {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.mock__bid {
  padding: 0.55rem 0;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-weight: 700;
  text-align: center;
}

.mock__bid--strong {
  border-color: transparent;
  background: var(--color-primary);
  color: var(--color-primary-contrast);
}

.mock__log {
  margin: 0.9rem 0 0;
  padding: 0.7rem 0 0;
  border-top: 1px solid var(--color-border);
  list-style: none;
  color: var(--color-muted);
  font-size: 0.8125rem;
}

.mock__log li + li {
  margin-top: 0.3rem;
}

.mock__log b {
  color: var(--color-text);
  font-weight: 600;
}
</style>
